// application/useCases/commitCoverages.js
/**
 * Flushes a coverage unit of work.
 *
 * The client id is stamped here rather than when the coverage is staged, which is what lets a
 * coverage be entered for a client that does not exist yet: the dialog creates the client
 * first, then commits with the id it got back.
 *
 * The whole set goes to the repository as ONE operation, because a reorder is a permutation
 * written across several documents. Applying it partially — three of four writes landing — can
 * leave two coverages sharing a `cobRank`, at which point the payer billed first is decided by
 * tie-break rather than by the user. The dialog closes even when a save fails, so there is no
 * retry to lean on: it has to be all or nothing.
 *
 * Pause decisions ride the same flush and the same transaction. They are a separate aggregate, so
 * strictly they could be written separately — but a coverage that saves without its pause is one
 * the user chose not to bill, silently billable, and the closing dialog would swallow the error.
 *
 * Removal is a soft delete — see the CoverageRepository port, and note that every reader of the
 * collection has to filter tombstones out.
 *
 * @param {import('../coverageUnitOfWork').CoverageUnitOfWork} unitOfWork
 * @param {{ clientId: string, clinicId: string }} scope
 * @param {import('../ports/CoverageRepository.port').CoverageRepository} coverageRepository
 * @returns {Promise<void>}
 */
// eslint-disable-next-line import/prefer-default-export
export async function commitCoverages(unitOfWork, { clientId, clinicId }, coverageRepository) {
  const changes = unitOfWork.changes();
  const intentChanges = unitOfWork.billingIntentChanges();

  if (changes.length === 0 && intentChanges.length === 0) {
    return;
  }

  await coverageRepository.applyChanges(
    changes.map(({ intent, coverage }) => ({
      intent,
      coverage: { ...coverage, clientId, clinicId },
    })),
    intentChanges.map(({ verb, billingIntent }) => ({
      verb,
      billingIntent: { ...billingIntent, clientId, clinicId },
    })),
  );

  unitOfWork.markCommitted();
} // commitCoverages()
